<template>
  <BaseModalEditCreate
    v-if="data"
    :mode="mode"
    :is-modified="isModified"
    :hide-save-button="!data.isFromManager"
    :disable="!data.isFromManager"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div :class="{ 'pointer-events-none': !data.isFromManager }">
        <label for="material" class="text-sm font-medium">{{ $t('material.material') }}</label>
        <BaseSearchDropdown
          v-model="selectedMaterial"
          :list-items="dropdownMaterials"
          size="sm"
          :noResultsText="$t('messages.noResultsText')"
          :count="materialCount"
          show-error-as-tooltip
          :error="getInputErrors('materialID')"
          @clear-selection="handleMaterialSelection"
          @updateSearch="updateMaterialsSearch"
          @item-selected="handleMaterialSelection" />
      </div>

      <div>
        <label for="distributionChannel" class="text-sm font-medium">{{
          $t('distributionChannel.distributionChannel')
        }}</label>
        <BaseSelectDropdown
          name="distributionChannel"
          :items="currentDistributionChannelDropdown"
          :selected-item="selectedDistributionChannel"
          disable
          show-error-as-tooltip
          :error="getInputErrors('distributionChannelID')"
          @update:selected="handleDistributionChannelSelection" />
      </div>

      <BaseToggle
        :label="$t('baseObject.isActive')"
        name="isActive"
        v-model="data.isActive"
        :class="{ 'pointer-events-none': !data.isFromManager }" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';
import { useLookupsStore } from '@/stores/lookups';

import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import BaseSearchDropdown from '@/components/common/BaseSearchDropdown.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import { getMaterialsWithConditions } from '@/services/api/materials';

import type { MaterialDC } from '@/types/MaterialDC';
import type { SelectDropdownItem } from '@/types/Base';

const { t: $t } = useI18n();

const emit = defineEmits<{
  closeModal: [];
  save: [];
}>();

const data = defineModel<MaterialDC>();

defineProps<{
  mode: 'add' | 'edit';
  isModified: boolean;
}>();

const lookupsStore = useLookupsStore();
const { dropdownDistributionChannels } = lookupsStore;

const dropdownMaterials = ref<SelectDropdownItem[]>([]);
const materialCount = ref(0);

const selectedMaterial = ref<SelectDropdownItem | null>(null);

const currentDistributionChannelDropdown = computed<SelectDropdownItem[]>(() => {
  if (!data.value?.distributionChannelID || !data.value?.distributionChannel) return [];

  const currentDC: SelectDropdownItem = {
    value: data.value.distributionChannelID,
    name: data.value.distributionChannel.company?.name || '',
  };

  return [currentDC];
});

const selectedDistributionChannel = computed<SelectDropdownItem | null>(() => {
  if (!data.value?.distributionChannelID) return null;

  return currentDistributionChannelDropdown.value[0] || null;
});

const validationModel = ref({
  materialID: data.value?.materialID || '',
  distributionChannelID: data.value?.distributionChannelID || '',
});

const rules = computed(() => ({
  materialID: {
    required: helpers.withMessage($t('messages.errors.required'), required),
  },
  distributionChannelID: {
    required: helpers.withMessage($t('messages.errors.required'), required),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

watch(
  () => data.value,
  (newData) => {
    if (!newData) return;
    validationModel.value.materialID = newData?.materialID || '';
    validationModel.value.distributionChannelID = newData?.distributionChannelID || '';

    // Set selected material if editing
    if (newData.materialID && newData.material?.name) {
      selectedMaterial.value = {
        value: newData.materialID,
        name: newData.material.name,
      };
    }
  },
  { deep: true, immediate: true }
);

async function updateMaterialsSearch(searchInput: string, page?: number) {
  const items = await getMaterialsWithConditions({
    page: page,
    itemsPerPage: 20,
    query: searchInput,
    sortColumn: 'name',
    sortAscending: true,
    includeDeleted: false,
    grouped: true,
  });
  if (items.count > 0 && items.data) {
    if (page && page > 1 && dropdownMaterials.value)
      dropdownMaterials.value = dropdownMaterials.value.concat(
        items.data.map((t) => ({
          value: t.id,
          name: t.name,
        })) as SelectDropdownItem[]
      );
    else
      dropdownMaterials.value = items.data.map((t) => ({
        value: t.id || '',
        name: t.name,
      }));
  } else dropdownMaterials.value = [];
  materialCount.value = items.count;
}

function handleMaterialSelection(item: SelectDropdownItem) {
  selectedMaterial.value = item;

  if (data.value) {
    data.value.materialID = item ? String(item.value) : '';
    if (item) {
      data.value.material = {
        id: String(item.value),
        name: item.name || '',
      };
    } else {
      data.value.material = undefined;
    }
  }
}

function handleDistributionChannelSelection(item: SelectDropdownItem | null) {
  if (item && data.value) {
    data.value.distributionChannelID = String(item.value);
    const dc = dropdownDistributionChannels.find((d) => d.value === item.value);
    if (dc) {
      data.value.distributionChannel = {
        id: String(item.value),
      };
    }
  }
}

function onDismissChanges() {
  if (data.value) {
    data.value = deepCopy<MaterialDC>(data.value);
  }
  emit('closeModal');
}

function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;

  emit('save');
}

function getInputErrors(type: 'materialID' | 'distributionChannelID') {
  if ($v.value[type].required.$invalid) return $v.value[type].required.$message;
  return '';
}
</script>
